import InfoIcon from '@mui/icons-material/Info';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { wz } from '../components/wizard-tokens';
import {
  WizCard,
  WizHeading,
  WizInfoBar,
  WizInput,
  WizLabel,
  WizLink,
  WizOption,
  WizSection,
  WizSelectPill,
  WizSwitch,
} from '../components/wizard-ui';
import {
  buildApproachGroups,
  isOptionSelected,
  optionPatch,
  PRIMARY_LANGUAGES,
  primaryLanguagePatch,
  secondaryLanguageOptions,
  TOGGLES,
} from './approach/approach-groups';
import AutoReplySettings from '../../campaign-builder/campaign-building-panel/campaign-flow-manager/auto-reply-Settings';
import CustomPlaceholders from '../../campaign-builder/campaign-building-panel/campaign-flow-manager/custom-placeholders';
import { ProductGroups } from 'enums';
import {
  autoReplyLanguagePatch,
  editAutoReply,
  resolveAutoReply,
  toggleAutoReply,
} from './approach/auto-reply';
import {
  addPlaceholder,
  autoReplyUsesMeetingLink,
  deletePlaceholder,
  editPlaceholder,
  freeKeySuggestions,
  isValidKey,
  normalizeKey,
} from './approach/placeholders';
import type { WizardStepProps } from './step.types';

/** Die Hinweise zu fehlenden Platzhaltern des alten Builders (`[[{{…}}]]`) gelten nur dort; die Vorgaben nutzen `{{…}}`. */
const NO_FIELDS_IN_USE = {};
const NO_CUSTOM_PLACEHOLDERS: never[] = [];
const noop = () => undefined;

const ACCOUNT_KEYS = {
  recruiter: 'wizApprAccountRecruiter',
  sales_navigator: 'wizApprAccountSalesNav',
  normal: 'wizApprAccountNormal',
} as const;

/** Schritt 5 „Ansprache“: Zielgruppe, Kontaktanfrage, Empfehlung, Sprache, Schalter. */
const StepApproach: React.FC<WizardStepProps> = ({ wizard }) => {
  const { t } = useTranslation();
  const { state, patch, account } = wizard;
  const productType = account?.productType ?? 'normal';
  const groups = buildApproachGroups(state, productType);
  const secondary = secondaryLanguageOptions(state.language);
  const [autoReplyOpen, setAutoReplyOpen] = useState(false);
  const autoReply = resolveAutoReply(state, wizard.autoReplyDefaults);
  const placeholders = state.customPlaceholders ?? [];
  const hasMeetingLink = state.calendar && state.calendarUrl.trim() !== '';

  return (
    <WizCard gap={24}>
      <WizHeading title={t('wizApprTitle')} subtitle={t('wizApprSubtitle')} />
      <WizInfoBar icon={<InfoIcon sx={{ fontSize: 16, color: wz.primary }} />}>
        {t('wizApprKnown', { info: t(ACCOUNT_KEYS[productType]), name: account?.name ?? '' })}
      </WizInfoBar>

      {groups.map(group => (
        <div key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <WizLabel>{t(group.titleKey)}</WizLabel>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: group.columns }}>
            {group.options.map(option => (
              <WizOption
                key={String(option.value)}
                selected={isOptionSelected(state, group.id, option.value)}
                onClick={() => patch(optionPatch(group.id, option.value))}
                label={t(option.labelKey)}
                description={option.descKey ? t(option.descKey) : undefined}
              />
            ))}
          </div>
        </div>
      ))}

      {state.referral ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: -12 }}>
          <div style={{ fontSize: 14, color: wz.muted }}>{t('wizApprBonusLabel')}</div>
          <div style={{ width: 160 }}>
            <WizInput
              small
              value={state.bonus}
              onChange={bonus => patch({ bonus })}
              invalid={wizard.showBlocker && wizard.blocker === 'bonus'}
              aria-label={t('wizApprBonusLabel')}
            />
          </div>
        </div>
      ) : null}

      <WizSection>
        <WizLabel>{t('wizApprLanguageTitle')}</WizLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 150, fontSize: 14, color: wz.muted }}>{t('wizApprLangPrimary')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PRIMARY_LANGUAGES.map(o => (
              <WizSelectPill
                key={o.value}
                selected={state.language === o.value}
                onClick={() =>
                  patch({
                    ...primaryLanguagePatch(o.value, state.language2),
                    ...autoReplyLanguagePatch(state, o.value, wizard.autoReplyDefaults),
                  })
                }
              >
                {t(o.labelKey)}
              </WizSelectPill>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 150, fontSize: 14, color: wz.muted, paddingTop: 6 }}>
            {t('wizApprLangSecondary')}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {secondary.map(o => (
                <WizSelectPill
                  key={o.value || 'none'}
                  selected={state.language2 === o.value}
                  onClick={() => patch({ language2: o.value })}
                >
                  {t(o.labelKey)}
                </WizSelectPill>
              ))}
            </div>
            <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>
              {t('wizApprLangSecondaryHint')}
            </div>
          </div>
        </div>
      </WizSection>

      <WizSection paddingTop={8} gap={0}>
        {TOGGLES.map(toggle => {
          const on = Boolean(state[toggle.field]);
          return (
            <div
              key={toggle.field}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '12px 0',
                borderBottom: `1px solid ${wz.borderSoft}`,
              }}
            >
              <WizSwitch
                on={on}
                onToggle={() => patch({ [toggle.field]: !on } as Record<string, boolean>)}
                label={t(toggle.labelKey)}
                description={'descKey' in toggle ? t(toggle.descKey) : undefined}
              />
              {toggle.field === 'calendar' && on ? (
                <div style={{ marginLeft: 48, maxWidth: 420 }}>
                  <WizInput
                    small
                    value={state.calendarUrl}
                    onChange={calendarUrl => patch({ calendarUrl })}
                    placeholder={t('wizApprCalendarPlaceholder')}
                    style={{ fontSize: 14 }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </WizSection>

      <WizSection paddingTop={8}>
        <WizLabel>{t('wizApprPlaceholdersTitle')}</WizLabel>
        <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.45 }}>
          {t('wizApprPlaceholdersHint', { link: '{{meetingLink}}', example: '{{Gehalt}}' })}
        </div>
        <CustomPlaceholders
          customPlaceholders={placeholders}
          handleAddCustomPlaceholder={(key, value) =>
            patch({ customPlaceholders: addPlaceholder(placeholders, key, value) })
          }
          handleDeleteCustomPlaceholder={key =>
            patch({ customPlaceholders: deletePlaceholder(placeholders, key) })
          }
          handleEditCustomPlaceholder={(original, key, value) =>
            patch({ customPlaceholders: editPlaceholder(placeholders, original, key, value) })
          }
          keySuggestions={ownKey => freeKeySuggestions(placeholders, ownKey)}
          keyError={(key, ownKey) =>
            !isValidKey(key)
              ? t('wizApprPlaceholderKeyInvalid')
              : placeholders.some(p => p.key !== ownKey && p.key.toLowerCase() === normalizeKey(key).toLowerCase())
                ? t('wizApprPlaceholderKeyUsed')
                : null
          }
          valueHint={t('wizApprPlaceholderValueHint')}
        />
      </WizSection>

      {autoReply ? (
        <WizSection paddingTop={8} gap={0}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0' }}>
            <WizSwitch
              on={autoReply.isActive}
              onToggle={() => patch({ autoReply: toggleAutoReply(autoReply) })}
              label={t('wizApprAutoReply')}
              description={t('wizApprAutoReplyDesc')}
            />
            {autoReply.isActive && autoReplyUsesMeetingLink(autoReply) && !hasMeetingLink ? (
              <div style={{ marginLeft: 48, fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>
                {t('wizApprAutoReplyNoCalendar')}
              </div>
            ) : null}
            <div style={{ marginLeft: 48 }}>
              <WizLink onClick={() => setAutoReplyOpen(open => !open)}>
                {autoReplyOpen ? t('wizApprAutoReplyHide') : t('wizApprAutoReplyShow')}
              </WizLink>
            </div>
            {autoReplyOpen ? (
              <div style={{ marginLeft: 48 }}>
                <AutoReplySettings
                  embedded
                  loading={false}
                  isActive={autoReply.isActive}
                  goalOfCommunication={autoReply.goalofCommunication}
                  caseHandling={autoReply.caseHandling}
                  onIsActiveChange={noop}
                  onGoalChange={e =>
                    patch({
                      autoReply: editAutoReply(autoReply, { goalofCommunication: e.target.value }),
                    })
                  }
                  onCaseHandlingUpdate={caseHandling =>
                    patch({ autoReply: editAutoReply(autoReply, { caseHandling }) })
                  }
                  handleUpdateAutoReply={noop}
                  funnelId={wizard.campaignId ?? undefined}
                  onBeforeTest={wizard.flushSave}
                  productGroup={ProductGroups.HR_BOOSTER}
                  fieldInUse={NO_FIELDS_IN_USE}
                  customPlaceholders={NO_CUSTOM_PLACEHOLDERS}
                />
              </div>
            ) : null}
          </div>
        </WizSection>
      ) : null}
    </WizCard>
  );
};

export default StepApproach;
